import { ReactNode } from 'react';
import { useScrollAnimation, useParallaxScroll } from '@/hooks/useScrollAnimation';
import { cn } from '@/lib/utils';

interface AnimatedSectionProps {
  children: ReactNode;
  className?: string;
  delay?: number;
  direction?: 'up' | 'down' | 'left' | 'right' | 'scale' | 'fade';
  duration?: 'fast' | 'normal' | 'slow';
  stagger?: boolean;
  parallax?: boolean;
  parallaxSpeed?: number;
}

export function AnimatedSection({ 
  children, 
  className,
  delay = 0,
  direction = 'up',
  duration = 'normal',
  stagger = false,
  parallax = false,
  parallaxSpeed = 0.15,
}: AnimatedSectionProps) {
  const { ref, isVisible } = useScrollAnimation({ threshold: 0.1 });
  const { ref: parallaxRef, offset } = useParallaxScroll({ speed: parallax ? parallaxSpeed : 0 });

  const directionStyles = {
    up: 'translate-y-16',
    down: '-translate-y-16',
    left: 'translate-y-12',
    right: 'translate-y-12',
    scale: 'scale-90',
    fade: '',
  };

  const durationStyles = {
    fast: 'duration-500',
    normal: 'duration-800',
    slow: 'duration-1200',
  };

  // Combine refs if parallax is enabled
  const combinedRef = (el: HTMLDivElement | null) => {
    if (ref) {
      (ref as React.MutableRefObject<HTMLDivElement | null>).current = el;
    }
    if (parallax && parallaxRef) {
      (parallaxRef as React.MutableRefObject<HTMLDivElement | null>).current = el;
    }
  };

  return (
    <div
      ref={combinedRef}
      className={cn(
        'transition-all ease-out will-change-transform',
        durationStyles[duration],
        isVisible 
          ? 'opacity-100 translate-x-0 translate-y-0 scale-100 blur-0' 
          : `opacity-0 ${directionStyles[direction]} blur-[3px]`,
        stagger && '[&>*]:transition-all [&>*]:duration-700 [&>*:nth-child(1)]:delay-0 [&>*:nth-child(2)]:delay-100 [&>*:nth-child(3)]:delay-200 [&>*:nth-child(4)]:delay-300',
        className
      )}
      style={{ 
        transitionDelay: `${delay}ms`,
        transform: parallax && isVisible ? `translateY(${offset}px)` : undefined,
      }}
    >
      {children}
    </div>
  );
}

// Parallax wrapper for illustrations
interface ParallaxImageProps {
  children: ReactNode;
  className?: string;
  speed?: number;
  direction?: 'up' | 'down';
}

export function ParallaxImage({ 
  children, 
  className,
  speed = 0.2,
  direction = 'up'
}: ParallaxImageProps) {
  const { ref, offset } = useParallaxScroll({ speed, direction });

  return (
    <div
      ref={ref}
      className={cn('will-change-transform transition-transform duration-100 ease-out', className)}
      style={{ 
        transform: `translateY(${offset}px)`,
      }}
    >
      {children}
    </div>
  );
}

